import { AuthContext } from "@/auth/authcontext";
import type { ApiError } from "@/lib/api";
import { api } from "@/lib/api";
import { UseQueryOptions } from "@tanstack/react-query";
import { useContext } from "react";
import { useNavigate } from "react-router";

export type AuthError = {
  status: "token_expired" | "invalid_token" | "invalid_otp";
  message: string;
};

export type User = {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  get_display_name?: string;
};

export type JWT = {
  access: string;
  refresh: string;
  user: User;
};

type LoginRequest = {
  email: string;
  password: string;
};

type LoginResponse = {
  status?: "success" | "otp_required";
  jwt?: JWT;
  tempOtpToken?: string;
};

type SignupRequest = {
  email: string;
  password1: string;
  first_name: string;
  last_name: string;
};

type SignupResponse = {
  access: string;
  refresh: string;
  user: User;
};

type SignupError = {
  message: string;
  errors?: {
    email?: string[];
    password1?: string[];
    password2?: string[];
    first_name?: string[];
    last_name?: string[];
  };
};

type OtpVerifyRequest = {
  otp: string;
  tempOtpToken: string;
};

type UpdateUserRequest = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

export function useUser(
  options?: Omit<UseQueryOptions<User, ApiError, User>, "queryKey" | "queryFn">
) {
  return api.useGet<User>("/api/auth/user/", options);
}

export function useLogin() {
  const navigate = useNavigate();
  const { setUserDetails } = useContext(AuthContext);

  return api.usePost<LoginResponse, LoginRequest>("/api/auth/login/", {
    onSuccess: (data) => {
      if (data.status === "success" && data.jwt) {
        localStorage.setItem("refreshToken", data.jwt.refresh);
        localStorage.setItem("accessToken", data.jwt.access);
        setUserDetails({
          access: data.jwt.access,
          refresh: data.jwt.refresh,
          user: data.jwt.user,
        });
        navigate("/");
      } else if (data.status === "otp_required" && data.tempOtpToken) {
        localStorage.setItem("tempOtpToken", data.tempOtpToken);
        navigate("/login/otp");
      }
    },
  });
}

export function useSignup() {
  const navigate = useNavigate();

  return api.usePost<SignupResponse, SignupRequest, SignupError>(
    "/api/auth/register/",
    {
      onSuccess: (data) => {
        localStorage.setItem("refreshToken", data.refresh);
        localStorage.setItem("accessToken", data.access);
        navigate("/");
      },
    }
  );
}

export function useVerifyOtp() {
  const navigate = useNavigate();

  return api.usePost<JWT, OtpVerifyRequest, AuthError>(
    "/api/auth/verify-otp/",
    {
      onSuccess: (data) => {
        localStorage.setItem("refreshToken", data.refresh);
        localStorage.setItem("accessToken", data.access);
        navigate("/");
      },
    }
  );
}

export function useRefreshToken() {
  return api.usePost<JWT, { refresh: string }>("/api/auth/token/refresh/");
}

export function useUpdateUser() {
  return api.usePatch<User, UpdateUserRequest>("/api/auth/user/");
}
